import { FormItemProps } from 'antd';
import { isNil, isString, isNumber, isArray, isPlainObject, isEmpty } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

type ValidateStatus = FormItemProps['validateStatus'];

export const computeValidateStatus = (value: unknown, validateStatus: ValidateStatus): ValidateStatus => {
  if (validateStatus === 'error' || validateStatus === 'validating') {
    return validateStatus;
  }

  if (isValueEmpty(value)) {
    return null;
  }

  return undefined;
};

export const formatDateToUTC = (date: Dayjs | null): string | null => {
  if (!date) return null;
  // Save as YYYY-MM-DDT00:00:00.000Z to ensure the exact date is preserved
  // without timezone shifts
  return `${date.format('YYYY-MM-DD')}T00:00:00.000Z`;
};

export const parseDateFromUTC = (value: string | null): Dayjs | undefined => {
  if (!value) return undefined;
  // Extract just the date part and create a local dayjs object
  const dateStr = value.substring(0, 10);
  return dayjs(dateStr, 'YYYY-MM-DD');
};

export const formatDateRangeToUTC = (dates: [Dayjs | null, Dayjs | null] | null): [string, string] | null => {
  if (!dates || !dates[0] || !dates[1]) return null;
  return [`${dates[0].format('YYYY-MM-DD')}T00:00:00.000Z`, `${dates[1].format('YYYY-MM-DD')}T00:00:00.000Z`];
};

export const parseDateRangeFromUTC = (value: [string, string] | null): [Dayjs, Dayjs] | [undefined, undefined] => {
  if (!value || !value[0] || !value[1]) return [undefined, undefined];
  const startDateStr = value[0].substring(0, 10);
  const endDateStr = value[1].substring(0, 10);
  return [dayjs(startDateStr, 'YYYY-MM-DD'), dayjs(endDateStr, 'YYYY-MM-DD')];
};

const isValueEmpty = (value: unknown): boolean => {
  if (isNil(value)) {
    return true;
  }

  if (isString(value)) {
    return isEmpty(value.trim());
  }

  if (isNumber(value)) {
    return false;
  }

  if (isArray(value)) {
    return isEmpty(value) || value.every(isNil);
  }

  if (isPlainObject(value)) {
    const obj = value as Record<string, unknown>;
    if ('amount' in obj) {
      return isNil(obj.amount);
    }
    return isEmpty(obj);
  }

  return false;
};
